import { useContext, useEffect, useState } from "react";
import axios from "axios";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { Nav } from "@/components/stax/Nav";
import { Hero } from "@/components/stax/Hero";
import { Marquee } from "@/components/stax/Marquee";
import { About } from "@/components/stax/About";
import { BuildCart } from "@/components/stax/BuildCart";
import { Services } from "@/components/stax/Services";
import { Portfolio } from "@/components/stax/Portfolio";
import { BrandKit } from "@/components/stax/BrandKit";
import { SpectrumLine } from "@/components/stax/SpectrumLine";
import { Testimonials } from "@/components/stax/Testimonials";
import { Freebies } from "@/components/stax/Freebies";
import { FAQ } from "@/components/stax/FAQ";
import { FinalCTA } from "@/components/stax/FinalCTA";
import { Footer } from "@/components/stax/Footer";
import { ContactDialog } from "@/components/stax/ContactDialog";
import { IntroLoader } from "@/components/stax/IntroLoader";
import { ContentCtx, fetchContent } from "@/lib/content";
import { Cursor } from "@/components/stax/Cursor";
import { ChatWidget } from "@/components/stax/ChatWidget";
import Admin from "@/pages/Admin";
import Invoice from "@/pages/Invoice";

const Home = () => {
  const [introDone, setIntroDone] = useState(false);
  const content = useContext(ContentCtx);
  const sec = content?.sections || {};
  const show = {
    work: !!sec.work,
    testimonials: !!sec.testimonials,
    freebies: !!sec.freebies,
  };

  return (
    <>
      <AnimatePresence>
        {!introDone && <IntroLoader onDone={() => setIntroDone(true)} />}
      </AnimatePresence>
      <Nav />
      <main>
        <Hero />
        <Marquee />
        <SpectrumLine />
        <About />
        <SpectrumLine />
        <BrandKit />
        <SpectrumLine />
        <Services />
        {show.work && (
          <>
            <SpectrumLine />
            <Portfolio />
          </>
        )}
        <SpectrumLine />
        <BuildCart />
        {show.testimonials && (
          <>
            <SpectrumLine />
            <Testimonials />
          </>
        )}
        {show.freebies && (
          <>
            <SpectrumLine />
            <Freebies />
          </>
        )}
        <SpectrumLine />
        <FAQ />
        <SpectrumLine />
        <FinalCTA />
      </main>
      <Footer />
      <ContactDialog />
    </>
  );
};

function App() {
  const [content, setContent] = useState(null);

  useEffect(() => {
    fetchContent().then((c) => {
      setContent(c);
      if (c?.colors) {
        const root = document.documentElement.style;
        if (c.colors.yellow) root.setProperty("--sy", c.colors.yellow);
        if (c.colors.magenta) root.setProperty("--sm", c.colors.magenta);
        if (c.colors.cyan) root.setProperty("--sc", c.colors.cyan);
      }
    });
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem("stax_tracked")) return;
    sessionStorage.setItem("stax_tracked", "1");
    axios
      .post(`${process.env.REACT_APP_BACKEND_URL}/api/track`, {
        path: window.location.pathname,
        referrer: document.referrer,
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const onClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const href = a.getAttribute("href");
      if (href && href.length > 1 && document.querySelector(href)) {
        e.preventDefault();
        lenis.scrollTo(href, { offset: -72 });
      }
    };
    document.addEventListener("click", onClick);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      document.removeEventListener("click", onClick);
    };
  }, []);

  return (
    <div className="App">
      <ContentCtx.Provider value={content}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/invoice/:id" element={<Invoice />} />
            <Route path="/admin" element={<Admin />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" toastOptions={{ style: { borderRadius: 0, border: "2px solid #0A0A0A", fontFamily: "'Archivo', sans-serif" } }} />
        <Cursor />
        <ChatWidget />
      </ContentCtx.Provider>
    </div>
  );
}

export default App;
